/**
 * Enterprise Engagements Component - ISOLATED UI for B2B engagement messaging
 * Displays conversations where B2B users text their AI friend and messages are
 * routed to Analytics Dashboard operators (talent users never see these messages)
 * Completely separate from existing conversation UI components
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import EngagementChatApiService, {
  EngagementMessage,
  B2BUserWithEngagements,
  AnalyticsDashboardView,
  CampaignConversation,
  CampaignMessage
} from '../services/engagementChatApi';


export function EnterpriseEngagements() {
  const [dashboardData, setDashboardData] = useState<AnalyticsDashboardView | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<EngagementMessage[]>([]);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Campaign chat state
  const [viewMode, setViewMode] = useState<'engagements' | 'campaigns'>('engagements');
  const [campaignConversations, setCampaignConversations] = useState<CampaignConversation[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [campaignMessages, setCampaignMessages] = useState<CampaignMessage[]>([]);
  
  // Load Analytics Dashboard view on mount
  useEffect(() => {
    if (viewMode === 'engagements') {
      loadDashboardView();
    } else {
      loadCampaignConversations();
    }
  }, [viewMode]);
  
  // Load messages when conversation selected
  useEffect(() => {
    if (viewMode === 'engagements' && selectedId) {
      loadMessages(selectedId);
    } else if (viewMode === 'campaigns' && selectedCampaignId) {
      loadCampaignMessages(selectedCampaignId);
    }
  }, [selectedId, selectedCampaignId, viewMode]);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, campaignMessages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const loadDashboardView = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await EngagementChatApiService.getAnalyticsDashboardView();
      setDashboardData(data);
      
      console.log(
        `Loaded Analytics Dashboard view: ${data.summary.total_b2b_users} B2B users, ` +
        `${data.summary.total_engagements} engagements, ` +
        `${data.summary.conversations_with_messages} with messages`
      );
      
      // Auto-expand first user
      if (data.b2b_users.length > 0) {
        setExpandedUsers(new Set([data.b2b_users[0].b2b_user.id]));
      }
    } catch (err) {
      console.error('Failed to load Analytics Dashboard view:', err);
      setError('Failed to load Analytics Dashboard view');
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleUserExpanded = (userId: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };
  
  const loadMessages = async (convId: number) => {
    try {
      const data = await EngagementChatApiService.getEngagementMessages(convId);
      setMessages(data);
      console.log(`Loaded ${data.length} messages for conversation ${convId}`);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    }
  };
  
  const loadCampaignConversations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const conversations = await EngagementChatApiService.getAllCampaignConversations();
      setCampaignConversations(conversations);
      console.log(`✅ Loaded ${conversations.length} campaign conversations`);
    } catch (err) {
      console.error('❌ Failed to load campaign conversations:', err);
      setError('Failed to load campaign conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCampaignMessages = async (convId: number) => {
    try {
      const data = await EngagementChatApiService.getCampaignMessages(convId);
      
      // Deduplicate messages by ID (safety check for backend duplicates)
      const uniqueMessages = Array.from(
        new Map(data.map(msg => [msg.id, msg])).values()
      );
      
      setCampaignMessages(uniqueMessages);
      console.log(`✅ Loaded ${uniqueMessages.length} campaign messages (${data.length - uniqueMessages.length} duplicates removed)`);
    } catch (err) {
      console.error('❌ Failed to load campaign messages:', err);
      setError('Failed to load campaign messages');
    }
  };

  const handleSendCampaignReply = async () => {
    if (!selectedCampaignId || !replyText.trim() || isSending) return;
    
    setIsSending(true);
    
    try {
      await EngagementChatApiService.sendCampaignReply(
        selectedCampaignId,
        replyText.trim()
      );
      
      console.log('✅ Campaign reply sent');
      setReplyText('');
      
      // Reload messages only - no need to reload entire conversations list
      await loadCampaignMessages(selectedCampaignId);
    } catch (err) {
      console.error('❌ Failed to send campaign reply:', err);
      setError('Failed to send campaign reply');
    } finally {
      setIsSending(false);
    }
  };
  
  const handleSendReply = async () => {
    if (!selectedId || !replyText.trim() || isSending) return;
    
    setIsSending(true);
    
    try {
      const response = await EngagementChatApiService.sendReplyToB2BUser(
        selectedId,
        replyText.trim()
      );
      
      console.log('Reply sent successfully:', response);
      
      // Clear input
      setReplyText('');
      
      // Reload messages to show the new reply
      await loadMessages(selectedId);
      
      // Reload dashboard view to update message counts
      await loadDashboardView();
      
    } catch (err) {
      console.error('Failed to send reply:', err);
      setError('Failed to send reply. Please try again.');
    } finally {
      setIsSending(false);
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };
  
  // Find selected conversation across all B2B users
  const selectedEngagement = dashboardData?.b2b_users
    .flatMap(user => user.engagements)
    .find(eng => eng.conversation?.id === selectedId);
  
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
              Enterprise Communications
            </h1>
            <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_sans-serif] mt-1">
              {viewMode === 'engagements' 
                ? 'Individual talent inquiries routed from iMessage'
                : 'Campaign match discussions with B2B users'}
            </p>
          </div>
          
          {/* Tab Switcher */}
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('engagements')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'engagements'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Engagements
              </button>
              <button
                onClick={() => setViewMode('campaigns')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'campaigns'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Campaign Chats
              </button>
            </div>
            
            {/* Refresh button */}
            <motion.button
              onClick={() => viewMode === 'engagements' ? loadDashboardView() : loadCampaignConversations()}
              disabled={isLoading}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
              ${isLoading 
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                : 'bg-white/20 dark:bg-gray-800/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 hover:bg-white/30 dark:hover:bg-gray-800/60'
              }
            `}
            whileHover={!isLoading ? { scale: 1.02 } : {}}
            whileTap={!isLoading ? { scale: 0.98 } : {}}
          >
            <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isLoading ? 'Refreshing...' : 'Refresh'}
            </motion.button>
          </div>
        </div>
        
        {/* Error banner */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Sidebar - Conversations List */}
        <div className="w-96 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : viewMode === 'campaigns' ? (
            // CAMPAIGN CONVERSATIONS LIST
            campaignConversations.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-lg mb-2">
                  No Campaign Chats
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Campaign conversations will appear here
                </p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {campaignConversations.map((conv) => (
                  <motion.div
                    key={conv.id}
                    onClick={() => setSelectedCampaignId(conv.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedCampaignId === conv.id
                        ? 'bg-purple-50 dark:bg-purple-950/30 border-2 border-purple-200 dark:border-purple-800 shadow-sm'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-800'
                    }`}
                    whileHover={{ scale: selectedCampaignId === conv.id ? 1 : 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <h4 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100">
                      {conv.b2b_user_email}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-1">
                      Campaign: {conv.campaign_query}
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                        {conv.selected_match_group}
                      </span>
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        💬 {conv.message_count} msgs
                      </span>
                    </div>
                    {conv.last_message_at && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {new Date(conv.last_message_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )
          ) : !dashboardData || dashboardData.b2b_users.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-lg mb-2">
                No Engagements Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Enterprise engagement conversations will appear here when B2B users start messaging
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {dashboardData!.b2b_users.map((userWithEngagements) => {
                const user = userWithEngagements.b2b_user;
                const engagements = userWithEngagements.engagements;
                const totalMessages = engagements.reduce((sum, eng) => sum + eng.messaging.message_count, 0);
                const totalUnread = engagements.reduce((sum, eng) => sum + eng.messaging.unread_from_b2b_user, 0);
                
                return (
                  <div key={user.id} className="space-y-2">
                    {/* B2B User Header */}
                    <motion.div
                      onClick={() => toggleUserExpanded(user.id)}
                      className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border border-purple-200 dark:border-purple-800 rounded-lg cursor-pointer hover:shadow-md transition-all"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <motion.svg
                            className="w-5 h-5 text-purple-600 dark:text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            animate={{ rotate: expandedUsers.has(user.id) ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </motion.svg>
                          
                          <div>
                            <h3 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100">
                              {user.email}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {engagements.length} engagement{engagements.length !== 1 ? 's' : ''} • 
                              {totalMessages > 0 && <span className="ml-1">{totalMessages} messages</span>}
                              {totalUnread > 0 && <span className="ml-1 text-blue-600 dark:text-blue-400">• {totalUnread} unread</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded-lg">
                            {user.credits} credits
                          </span>
                          <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-lg">
                            B2B User
                          </span>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Nested Engagements */}
                    <AnimatePresence>
                      {expandedUsers.has(user.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-6 space-y-2 overflow-hidden"
                        >
                          {engagements.map((engagement) => {
                            const conv = engagement.conversation;
                            const messaging = engagement.messaging;
                            
                            // Skip if no conversation created yet
                            if (!conv) {
                              return (
                                <div
                                  key={engagement.engagement_id}
                                  className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700"
                                >
                                  <h4 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-medium text-gray-600 dark:text-gray-400 text-sm">
                                    {engagement.target_user.name}
                                  </h4>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Engagement created - no messaging yet
                                  </p>
                                </div>
                              );
                            }
                            
                            return (
                              <motion.div
                                key={conv.id}
                                onClick={() => setSelectedId(conv.id)}
                                className={`
                                  p-3 rounded-lg cursor-pointer transition-all duration-200 relative
                                  ${selectedId === conv.id
                                    ? 'bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-200 dark:border-blue-800 shadow-sm'
                                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800'
                                  }
                                `}
                                whileHover={{ scale: selectedId === conv.id ? 1 : 1.01 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                {/* Unread indicator */}
                                {messaging.unread_from_b2b_user > 0 && (
                                  <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                )}
                                
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-medium text-gray-900 dark:text-gray-100 text-sm">
                                      {engagement.target_user.name}
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                      via {conv.b2b_ai_friend_name}
                                    </p>
                                    
                                    {/* Message status */}
                                    {messaging.has_messages ? (
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className="text-xs text-blue-600 dark:text-blue-400">
                                          💬 {messaging.message_count} message{messaging.message_count !== 1 ? 's' : ''}
                                        </span>
                                        {messaging.unread_from_b2b_user > 0 && (
                                          <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                                            {messaging.unread_from_b2b_user} new
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex flex-col gap-1 mt-2">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          No messages yet
                                        </p>
                                        {!conv.b2b_ai_friend_phone && (
                                          <span className="text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                            ⚠️ AI friend has no phone
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                  {new Date(conv.updated_at).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Right Panel - Chat Interface */}
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950">
          {viewMode === 'campaigns' && selectedCampaignId ? (
            // CAMPAIGN CHAT INTERFACE
            <>
              {/* Campaign Chat Header */}
              <div className="p-6 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xl">
                      Campaign Chat
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      B2B User: {campaignConversations.find(c => c.id === selectedCampaignId)?.b2b_user_email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-lg">
                        {campaignConversations.find(c => c.id === selectedCampaignId)?.selected_match_group}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {campaignMessages.length} messages
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Campaign Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {campaignMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p>No messages yet</p>
                      <p className="text-sm mt-1">Start the conversation with the B2B user</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {campaignMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.message_type === 'analytics' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className="max-w-md">
                          <div
                            className={`
                              px-4 py-3 rounded-2xl shadow-sm
                              ${msg.message_type === 'analytics'
                                ? 'bg-purple-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                              }
                            `}
                          >
                            <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-base leading-relaxed">
                              {msg.content}
                            </p>
                          </div>
                          
                          {/* Timestamp and metadata */}
                          <div className={`mt-1 px-2 text-xs ${
                            msg.message_type === 'analytics' 
                              ? 'text-right text-gray-500 dark:text-gray-400' 
                              : 'text-left text-gray-500 dark:text-gray-400'
                          }`}>
                            <span>
                              {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZone: 'America/New_York'
                              })}
                            </span>
                            {msg.message_type === 'user' && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400">
                                via {msg.source === 'sms' ? 'SMS' : 'Web'}
                              </span>
                            )}
                            {msg.message_type === 'analytics' && msg.delivered && (
                              <span className="ml-2 text-green-600 dark:text-green-400">
                                ✓ Delivered
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Campaign Reply Input */}
              <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendCampaignReply()}
                    placeholder="Reply to B2B user..."
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                    disabled={isSending}
                  />
                  
                  <motion.button
                    onClick={handleSendCampaignReply}
                    disabled={isSending || !replyText.trim()}
                    className={`
                      px-6 py-3 rounded-xl font-medium transition-all duration-200
                      ${isSending || !replyText.trim()
                        ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600 shadow-lg hover:shadow-xl'
                      }
                    `}
                    whileHover={!isSending && replyText.trim() ? { scale: 1.02 } : {}}
                    whileTap={!isSending && replyText.trim() ? { scale: 0.98 } : {}}
                  >
                    {isSending ? 'Sending...' : 'Send via SMS'}
                  </motion.button>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Messages sent here will be delivered to the B2B user via SMS
                </p>
              </div>
            </>
          ) : selectedEngagement ? (
            <>
              {/* Chat Header */}
              <div className="p-6 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xl">
                      {selectedEngagement.target_user.name}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      B2B User texting via {selectedEngagement.conversation.b2b_ai_friend_name}
                    </p>
                    
                    {/* Message count indicator */}
                    {selectedEngagement.messaging.has_messages && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-lg">
                          {selectedEngagement.messaging.message_count} messages
                        </span>
                        {selectedEngagement.messaging.unread_from_b2b_user > 0 && (
                          <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded-lg">
                            {selectedEngagement.messaging.unread_from_b2b_user} from B2B user
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-2 justify-end">
                      <span>AI Friend: {selectedEngagement.conversation.b2b_ai_friend_phone || 'No phone'}</span>
                      {!selectedEngagement.conversation.b2b_ai_friend_phone && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                          ⚠️
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-1">Chat ID: {selectedEngagement.conversation.linq_chat_id || 'Pending'}</div>
                  </div>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p>No messages yet</p>
                      <p className="text-sm mt-1">Waiting for B2B user to send first message</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'analytics_operator' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className="max-w-md">
                          <div
                            className={`
                              px-4 py-3 rounded-2xl shadow-sm
                              ${msg.sender_type === 'analytics_operator'
                                ? 'bg-blue-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                              }
                            `}
                          >
                            <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-base leading-relaxed">
                              {msg.content}
                            </p>
                          </div>
                          
                          {/* Timestamp and metadata */}
                          <div className={`mt-1 px-2 text-xs ${
                            msg.sender_type === 'analytics_operator' 
                              ? 'text-right text-gray-500 dark:text-gray-400' 
                              : 'text-left text-gray-500 dark:text-gray-400'
                          }`}>
                            <span>
                              {new Date(msg.created_at).toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                            {msg.sender_type === 'b2b_user' && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400">
                                via iMessage
                              </span>
                            )}
                            {msg.sender_type === 'analytics_operator' && msg.sent_via_imessage && (
                              <span className="ml-2 text-green-600 dark:text-green-400">
                                ✓ Delivered
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Reply Input */}
              <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Reply to B2B user as ${selectedEngagement.conversation.b2b_ai_friend_name}...`}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    disabled={isSending}
                  />
                  
                  <motion.button
                    onClick={handleSendReply}
                    disabled={isSending || !replyText.trim()}
                    className={`
                      px-6 py-3 rounded-xl font-medium transition-all duration-200
                      ${isSending || !replyText.trim()
                        ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:shadow-xl'
                      }
                    `}
                    whileHover={!isSending && replyText.trim() ? { scale: 1.02 } : {}}
                    whileTap={!isSending && replyText.trim() ? { scale: 0.98 } : {}}
                  >
                    {isSending ? 'Sending...' : 'Send via iMessage'}
                  </motion.button>
                </div>
                
                {selectedEngagement.conversation.b2b_ai_friend_phone ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Messages sent here will be delivered to the B2B user via iMessage from their AI friend.
                    The talent user will never see these messages.
                  </p>
                ) : (
                  <p className="text-xs bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 p-2 rounded mt-2">
                    ⚠️ This AI friend has no phone number assigned. Messages cannot be sent until a phone is configured in VectorDB.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h3 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-lg mb-2">
                  Select a Conversation
                </h3>
                <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Choose an engagement from the list to view messages and reply
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

